import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import './CourseContentManager.css';

const emptyFormState = (contentType = 'VIDEO') => ({
    title: '',
    description: '',
    contentType: contentType || 'VIDEO',
    contentOrder: 1,
    isFree: false,
    isPremium: false,
    videoSource: 'url',
    videoUrl: '',
    videoDuration: '',
    videoFile: null,
    documentSource: 'url',
    documentUrl: '',
    documentFile: null,
    documentType: '',
    documentSize: '',
    problemStatement: '',
    solution: '',
    solutionCode: '',
    initialCode: '',
    sourceLanguage: 'java',
    initialCodeJava: '',
    initialCodePython: '',
    initialCodeJavaScript: '',
    initialCodeCpp: '',
    initialCodeC: '',
    expectedOutput: '',
    testCases: '',
    hiddenTestCases: '',
    constraints: '',
    examples: '',
    relatedTopics: '',
    followUp: '',
    difficultyLevel: 'MEDIUM',
    timeLimitSeconds: '',
    memoryLimitMB: '',
    categoryTags: '',
    videoLink: ''
});

function mapEditPayloadToForm(content, question) {
    const base = emptyFormState(content.contentType);
    return {
        ...base,
        title: content.title || '',
        description: content.description || '',
        contentType: content.contentType || 'VIDEO',
        contentOrder: content.contentOrder ?? 1,
        isFree: !!content.isFree,
        isPremium: !!content.isPremium,
        videoSource: content.videoUrl ? 'url' : 'upload',
        videoUrl: content.videoUrl || '',
        videoDuration: content.videoDuration != null ? String(content.videoDuration) : '',
        videoFile: null,
        documentSource: content.documentUrl ? 'url' : 'upload',
        documentUrl: content.documentUrl || '',
        documentFile: null,
        documentType: content.documentType || '',
        documentSize: content.documentSize != null ? String(content.documentSize) : '',
        ...(question
            ? {
                  problemStatement: question.problemStatement || '',
                  solution: question.solution || '',
                  solutionCode: question.solutionCode || '',
                  initialCode: question.initialCode || '',
                  sourceLanguage: question.sourceLanguage || 'java',
                  initialCodeJava: question.solutionCodeJava || '',
                  initialCodePython: question.solutionCodePython || '',
                  initialCodeJavaScript: question.solutionCodeJavaScript || '',
                  initialCodeCpp: question.solutionCodeCpp || '',
                  initialCodeC: question.solutionCodeC || '',
                  expectedOutput: question.expectedOutput || '',
                  testCases: question.testCases || '',
                  hiddenTestCases: '',
                  constraints: question.constraints || '',
                  examples: question.examples || '',
                  relatedTopics: question.relatedTopics || '',
                  followUp: question.followUp || '',
                  difficultyLevel: question.difficultyLevel || 'MEDIUM',
                  timeLimitSeconds: question.timeLimitSeconds != null ? String(question.timeLimitSeconds) : '',
                  memoryLimitMB: question.memoryLimitMB != null ? String(question.memoryLimitMB) : '',
                  categoryTags: question.categoryTags || '',
                  videoLink: question.videoLink || ''
              }
            : {})
    };
}

function buildQuestionApiBody(contentData, sectionId) {
    return {
        title: contentData.title,
        description: contentData.description,
        problemStatement: contentData.problemStatement,
        solution: contentData.solution,
        solutionCode: contentData.solutionCode,
        initialCode: contentData.initialCode,
        sourceLanguage: contentData.sourceLanguage,
        solutionCodeJava: contentData.initialCodeJava,
        solutionCodePython: contentData.initialCodePython,
        solutionCodeJavaScript: contentData.initialCodeJavaScript,
        solutionCodeCpp: contentData.initialCodeCpp,
        solutionCodeC: contentData.initialCodeC,
        expectedOutput: contentData.expectedOutput,
        testCases: contentData.testCases,
        constraints: contentData.constraints,
        examples: contentData.examples,
        relatedTopics: contentData.relatedTopics,
        followUp: contentData.followUp,
        difficultyLevel: contentData.difficultyLevel,
        timeLimitSeconds: contentData.timeLimitSeconds ? parseInt(contentData.timeLimitSeconds, 10) : null,
        memoryLimitMB: contentData.memoryLimitMB ? parseInt(contentData.memoryLimitMB, 10) : null,
        categoryTags: contentData.categoryTags,
        videoLink: contentData.videoLink,
        questionOrder: contentData.contentOrder ? parseInt(contentData.contentOrder, 10) : null,
        isPremium: contentData.isPremium,
        courseSection: { id: sectionId }
    };
}

function sanitizeCourseContentJson(body) {
    const { videoFile, documentFile, videoSource, documentSource, ...rest } = body;
    const out = {
        ...rest,
        contentOrder:
            rest.contentOrder !== '' && rest.contentOrder != null
                ? parseInt(rest.contentOrder, 10)
                : null,
        videoDuration:
            rest.videoDuration !== '' && rest.videoDuration != null
                ? parseInt(rest.videoDuration, 10)
                : null,
        documentSize:
            rest.documentSize !== '' && rest.documentSize != null
                ? parseInt(rest.documentSize, 10)
                : null
    };
    return out;
}

const CourseContentManager = ({ course, onBack }) => {
    const [sections, setSections] = useState([]);
    const [contents, setContents] = useState({});
    const [questions, setQuestions] = useState({});
    const [sessions, setSessions] = useState({}); // Live sessions by section ID
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showSessionForm, setShowSessionForm] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [contentType, setContentType] = useState('VIDEO');
    const [message, setMessage] = useState('');
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [editingPayload, setEditingPayload] = useState(null);

    useEffect(() => {
        fetchSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course.id]);

    const fetchSections = async () => {
        try {
            console.log('Fetching sections for course:', course.id, course.title);
            const response = await fetch(`${API_URL}/api/course-sections/course/${course.id}`);
            console.log('Sections response status:', response.status);
            
            if (!response.ok) {
                console.error('Failed to fetch sections:', response.status, response.statusText);
                setLoading(false);
                return;
            }
            
            const sectionsData = await response.json();
            console.log('Sections data:', sectionsData);
            setSections(sectionsData);
            
            // Fetch content and questions for each section
            const contentsData = {};
            const questionsData = {};
            for (const section of sectionsData) {
                try {
                    console.log('Fetching content for section:', section.id, section.title);
                    const contentsResponse = await fetch(`${API_URL}/api/course-contents/section/${section.id}`);
                    if (contentsResponse.ok) {
                        const sectionContents = await contentsResponse.json();
                        console.log('Content for section', section.id, ':', sectionContents);
                        contentsData[section.id] = sectionContents;
                    } else {
                        console.error('Failed to fetch content for section', section.id, ':', contentsResponse.status);
                        contentsData[section.id] = [];
                    }
                } catch (error) {
                    console.error(`Error fetching contents for section ${section.id}:`, error);
                    contentsData[section.id] = [];
                }

                try {
                    console.log('Fetching questions for section:', section.id, section.title);
                    const questionsResponse = await fetch(`${API_URL}/api/course-questions/section/${section.id}`);
                    if (questionsResponse.ok) {
                        const sectionQuestions = await questionsResponse.json();
                        console.log('Questions for section', section.id, ':', sectionQuestions);
                        questionsData[section.id] = sectionQuestions;
                    } else {
                        console.error('Failed to fetch questions for section', section.id, ':', questionsResponse.status);
                        questionsData[section.id] = [];
                    }
                } catch (error) {
                    console.error(`Error fetching questions for section ${section.id}:`, error);
                    questionsData[section.id] = [];
                }
            }
            setContents(contentsData);
            setQuestions(questionsData);
            
            // Fetch live sessions for each section (if live course)
            if (course.courseType === 'LIVE') {
                const sessionsData = {};
                for (const section of sectionsData) {
                    try {
                        const sessionResponse = await fetch(`${API_URL}/api/live-sessions/section/${section.id}`);
                        if (sessionResponse.ok && sessionResponse.status !== 204) {
                            const session = await sessionResponse.json();
                            sessionsData[section.id] = session;
                        }
                    } catch (error) {
                        console.error(`Error fetching session for section ${section.id}:`, error);
                    }
                }
                setSessions(sessionsData);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching sections:', error);
            setLoading(false);
        }
    };

    const handleAddContent = (section) => {
        setEditingPayload(null);
        setSelectedSection(section);
        setContentType('VIDEO');
        setShowAddForm(true);
    };

    const handleAddQuestion = (section) => {
        setEditingPayload(null);
        setSelectedSection(section);
        setContentType('QUESTION');
        setShowAddForm(true);
    };

    const handleEditContent = async (content, section) => {
        try {
            const res = await fetch(`${API_URL}/api/course-contents/${content.id}`);
            if (!res.ok) {
                setMessage('Could not load content to edit.');
                setTimeout(() => setMessage(''), 4000);
                return;
            }
            const fullContent = await res.json();
            let question = null;
            if (fullContent.contentType === 'QUESTION') {
                const qid = fullContent.questionId ?? fullContent.question?.id;
                if (qid) {
                    const qr = await fetch(`${API_URL}/api/course-questions/${qid}`);
                    if (qr.ok) {
                        question = await qr.json();
                    }
                }
            }
            setSelectedSection(section);
            setContentType(fullContent.contentType || 'VIDEO');
            setEditingPayload({ content: fullContent, question });
            setShowAddForm(true);
        } catch (e) {
            console.error(e);
            setMessage('Error loading content: ' + e.message);
            setTimeout(() => setMessage(''), 4000);
        }
    };

    const handleEditQuestion = async (question, section) => {
        const list = contents[section.id] || [];
        const linked = list.find(
            (c) =>
                c.contentType === 'QUESTION' &&
                (c.questionId === question.id || c.question?.id === question.id)
        );
        if (linked) {
            handleEditContent(linked, section);
        } else {
            setMessage('Could not find course content entry for this question. Try refreshing the page.');
            setTimeout(() => setMessage(''), 5000);
        }
    };

    const handleSaveContent = async (contentData) => {
        const token = localStorage.getItem('token');
        const isEdit = editingPayload != null;

        const finishSuccess = () => {
            setMessage(isEdit ? 'Content updated successfully!' : 'Content added successfully!');
            setShowAddForm(false);
            setSelectedSection(null);
            setEditingPayload(null);
            fetchSections();
            setTimeout(() => setMessage(''), 3000);
        };

        try {
            if (isEdit && !editingPayload?.content?.id) {
                setMessage('Edit session expired. Cancel and open Edit again.');
                return;
            }
            if (contentData.contentType === 'QUESTION' && isEdit && !editingPayload?.question) {
                setMessage('Question data could not be loaded. Cancel and try Edit again.');
                return;
            }

            if (contentData.contentType === 'QUESTION') {
                const qBody = buildQuestionApiBody(contentData, selectedSection.id);

                if (isEdit && editingPayload?.question) {
                    const quRes = await fetch(`${API_URL}/api/course-questions/${editingPayload.question.id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(qBody)
                    });
                    if (!quRes.ok) {
                        const errorText = await quRes.text();
                        setMessage('Failed to update question: ' + errorText);
                        setTimeout(() => setMessage(''), 4000);
                        return;
                    }
                    const cPayload = sanitizeCourseContentJson({
                        title: contentData.title,
                        description: contentData.description,
                        contentType: 'QUESTION',
                        contentOrder: contentData.contentOrder,
                        isFree: contentData.isFree,
                        isPremium: contentData.isPremium,
                        courseSection: { id: selectedSection.id },
                        question: { id: editingPayload.question.id }
                    });
                    const cRes = await fetch(`${API_URL}/api/course-contents/${editingPayload.content.id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(cPayload)
                    });
                    if (!cRes.ok) {
                        const errorText = await cRes.text();
                        setMessage('Failed to update content: ' + errorText);
                        setTimeout(() => setMessage(''), 4000);
                        return;
                    }
                    finishSuccess();
                    return;
                }

                const questionResponse = await fetch(`${API_URL}/api/course-questions`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(qBody)
                });

                if (!questionResponse.ok) {
                    const errorText = await questionResponse.text();
                    console.error('Failed to create question:', errorText);
                    setMessage('Failed to create question');
                    setTimeout(() => setMessage(''), 3000);
                    return;
                }

                const question = await questionResponse.json();
                const response = await fetch(`${API_URL}/api/course-contents`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(
                        sanitizeCourseContentJson({
                            title: contentData.title,
                            description: contentData.description,
                            contentType: 'QUESTION',
                            contentOrder: contentData.contentOrder,
                            isFree: contentData.isFree,
                            isPremium: contentData.isPremium,
                            question: { id: question.id },
                            courseSection: { id: selectedSection.id }
                        })
                    )
                });

                if (response.ok) {
                    await response.json();
                    finishSuccess();
                } else {
                    const errorText = await response.text();
                    setMessage('Failed to add content: ' + errorText);
                    setTimeout(() => setMessage(''), 3000);
                }
                return;
            }

            let requestBody;

            if (contentData.contentType === 'VIDEO' && contentData.videoSource === 'upload' && contentData.videoFile) {
                setUploadingVideo(true);
                setMessage('Uploading video to cloud storage...');

                const formData = new FormData();
                formData.append('file', contentData.videoFile);

                const uploadResponse = await fetch(`${API_URL}/api/upload/video`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: formData
                });

                setUploadingVideo(false);

                if (uploadResponse.ok) {
                    const uploadResult = await uploadResponse.json();
                    setMessage('Video uploaded! Saving content...');
                    requestBody = {
                        ...contentData,
                        videoUrl: uploadResult.cloudHosted ? uploadResult.url : `${API_URL}${uploadResult.url}`,
                        videoDuration: Math.floor((contentData.videoFile.size / 1024 / 1024) * 60),
                        courseSection: { id: selectedSection.id }
                    };
                } else {
                    const errorText = await uploadResponse.text();
                    setMessage('Failed to upload video: ' + errorText);
                    setTimeout(() => setMessage(''), 3000);
                    return;
                }
            } else if (contentData.contentType === 'DOCUMENT' && contentData.documentSource === 'upload' && contentData.documentFile) {
                setUploadingVideo(true);
                setMessage('Uploading document...');

                const formData = new FormData();
                formData.append('file', contentData.documentFile);

                const uploadResponse = await fetch(`${API_URL}/api/upload/document`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: formData
                });

                setUploadingVideo(false);

                if (uploadResponse.ok) {
                    const uploadResult = await uploadResponse.json();
                    setMessage('Document uploaded! Saving content...');
                    requestBody = {
                        ...contentData,
                        documentUrl: uploadResult.cloudHosted ? uploadResult.url : `${API_URL}${uploadResult.url}`,
                        documentType:
                            contentData.documentFile.name.split('.').pop()?.toLowerCase() || contentData.documentType,
                        documentSize: contentData.documentFile.size,
                        courseSection: { id: selectedSection.id }
                    };
                } else {
                    const errorText = await uploadResponse.text();
                    setMessage('Failed to upload document: ' + errorText);
                    setTimeout(() => setMessage(''), 3000);
                    return;
                }
            } else {
                requestBody = {
                    ...contentData,
                    courseSection: { id: selectedSection.id }
                };
            }

            const jsonBody = sanitizeCourseContentJson(requestBody);
            const url = isEdit
                ? `${API_URL}/api/course-contents/${editingPayload.content.id}`
                : `${API_URL}/api/course-contents`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(jsonBody)
            });

            if (response.ok) {
                await response.json();
                finishSuccess();
            } else {
                const errorText = await response.text();
                setMessage('Failed to save content: ' + errorText);
                setTimeout(() => setMessage(''), 4000);
            }
        } catch (error) {
            console.error('Error saving content:', error);
            setMessage('Error saving content: ' + error.message);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const handleDeleteContent = async (contentId) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/course-contents/${contentId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (response.ok) {
                    setMessage('Content deleted successfully!');
                    fetchSections();
                    setTimeout(() => setMessage(''), 3000);
                } else {
                    setMessage('Failed to delete content');
                    setTimeout(() => setMessage(''), 3000);
                }
            } catch (error) {
                setMessage('Error deleting content');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/course-questions/${questionId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (response.ok) {
                    setMessage('Question deleted successfully!');
                    fetchSections();
                    setTimeout(() => setMessage(''), 3000);
                } else {
                    setMessage('Failed to delete question');
                    setTimeout(() => setMessage(''), 3000);
                }
            } catch (error) {
                setMessage('Error deleting question');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    const handleScheduleSession = (section) => {
        setSelectedSection(section);
        setShowSessionForm(true);
    };

    const handleDeleteSession = async (sessionId, sectionId) => {
        if (window.confirm('Are you sure you want to delete this scheduled session?')) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/live-sessions/${sessionId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                if (response.ok) {
                    setMessage('Session deleted successfully!');
                    // Remove from state
                    const newSessions = { ...sessions };
                    delete newSessions[sectionId];
                    setSessions(newSessions);
                    setTimeout(() => setMessage(''), 3000);
                } else {
                    setMessage('Failed to delete session');
                    setTimeout(() => setMessage(''), 3000);
                }
            } catch (error) {
                setMessage('Error deleting session');
                setTimeout(() => setMessage(''), 3000);
            }
        }
    };

    const getContentTypeIcon = (contentType) => {
        switch (contentType) {
            case 'VIDEO': return '📹';
            case 'DOCUMENT': return '📄';
            case 'QUESTION': return '💻';
            default: return '📝';
        }
    };

    const getContentTypeColor = (contentType) => {
        switch (contentType) {
            case 'VIDEO': return 'bg-red-500';
            case 'DOCUMENT': return 'bg-blue-500';
            case 'QUESTION': return 'bg-green-500';
            default: return 'bg-gray-500';
        }
    };

    if (loading) {
        return (
            <div className="content-manager">
                <div className="loading">Loading course content...</div>
            </div>
        );
    }

    if (showAddForm) {
        return (
            <AddContentForm
                section={selectedSection}
                contentType={contentType}
                editMode={!!editingPayload}
                initialEditPayload={editingPayload}
                onSave={handleSaveContent}
                onCancel={() => {
                    setShowAddForm(false);
                    setSelectedSection(null);
                    setEditingPayload(null);
                }}
                uploadingVideo={uploadingVideo}
                setUploadingVideo={setUploadingVideo}
                setMessage={setMessage}
            />
        );
    }

    if (showSessionForm) {
        return (
            <SessionForm
                course={course}
                section={selectedSection}
                existingSession={sessions[selectedSection?.id]}
                onSave={(session) => {
                    setSessions({...sessions, [selectedSection.id]: session});
                    setShowSessionForm(false);
                    setSelectedSection(null);
                    setMessage('Session scheduled successfully!');
                    setTimeout(() => setMessage(''), 3000);
                }}
                onCancel={() => {
                    setShowSessionForm(false);
                    setSelectedSection(null);
                }}
                setMessage={setMessage}
            />
        );
    }

    return (
        <div className="content-manager">
            <div className="header">
                <button onClick={onBack} className="back-btn">
                    ← Back to Course Management
                </button>
                <h2>Manage Content for: {course.title}</h2>
            </div>

            {message && (
                <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
                    {message}
                </div>
            )}

            <div className="sections-container">
                {sections.map((section, index) => (
                    <div key={section.id} className="section-card">
                        <div className="section-header">
                            <h3>Section {index + 1}: {section.title}</h3>
                            <div className="section-actions">
                                <button 
                                    onClick={() => handleAddContent(section)}
                                    className="add-content-btn"
                                >
                                    + Add Content
                                </button>
                                <button 
                                    onClick={() => handleAddQuestion(section)}
                                    className="add-question-btn"
                                >
                                    + Add Question
                                </button>
                                {/* Schedule Session Button for Live Courses */}
                                {course.courseType === 'LIVE' && (
                                    <button 
                                        onClick={() => handleScheduleSession(section)}
                                        className="add-btn"
                                        style={{
                                            background: sessions[section.id] ? '#f59e0b' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                                            color: 'white'
                                        }}
                                    >
                                        {sessions[section.id] ? '📅 Edit Session' : '📅 Schedule Session'}
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* Live Session Display (for live courses) */}
                        {course.courseType === 'LIVE' && (
                            <div style={{marginBottom: '20px'}}>
                                {sessions[section.id] ? (
                                    <div style={{padding: '15px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '8px', border: '2px solid #fbbf24', marginTop: '15px'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                                            <div style={{flex: 1}}>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px'}}>
                                                    <span style={{fontSize: '24px'}}>📅</span>
                                                    <strong style={{color: '#92400e', fontSize: '16px'}}>Scheduled Live Session</strong>
                                                    <span style={{background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold'}}>
                                                        {sessions[section.id].status}
                                                    </span>
                                                </div>
                                                <div style={{fontSize: '14px', color: '#78350f', marginLeft: '32px', lineHeight: '1.8'}}>
                                                    <div><strong>Title:</strong> {sessions[section.id].title}</div>
                                                    {sessions[section.id].description && (
                                                        <div><strong>Description:</strong> {sessions[section.id].description}</div>
                                                    )}
                                                    <div><strong>📍 Date:</strong> {new Date(sessions[section.id].scheduledDate).toLocaleString()}</div>
                                                    <div><strong>⏱️ Duration:</strong> {sessions[section.id].durationMinutes} minutes</div>
                                                    <div><strong>💻 Platform:</strong> {sessions[section.id].meetingPlatform}</div>
                                                    {sessions[section.id].topicsCovered && (
                                                        <div><strong>📚 Topics:</strong> {sessions[section.id].topicsCovered}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleDeleteSession(sessions[section.id].id, section.id)}
                                                className="delete-btn"
                                                style={{background: '#dc2626', color: 'white', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold'}}
                                            >
                                                🗑️ Delete
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{padding: '12px', background: '#fee2e2', borderRadius: '6px', fontSize: '14px', color: '#991b1b', marginTop: '15px', textAlign: 'center'}}>
                                        ⚠️ No live session scheduled for this section yet. Click "Schedule Session" to add one.
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="content-list">
                            {/* Display Questions */}
                            {questions[section.id] && questions[section.id].length > 0 && (
                                <div className="questions-section">
                                    <h4>Questions ({questions[section.id].length})</h4>
                                    {questions[section.id].map((question) => (
                                        <div key={question.id} className="content-item question-item">
                                            <div className="content-info">
                                                <span className={`content-type bg-green-500`}>
                                                    💻 QUESTION
                                                </span>
                                                <h4>{question.title}</h4>
                                                <p>{question.description}</p>
                                                <p><strong>Difficulty:</strong> {question.difficultyLevel}</p>
                                                {question.isPremium && (
                                                    <span className="premium-badge">PREMIUM</span>
                                                )}
                                            </div>
                                            <div className="content-actions">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditQuestion(question, section)}
                                                    className="view-btn"
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteQuestion(question.id)}
                                                    className="delete-btn"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Display Other Content */}
                            {contents[section.id] && contents[section.id].length > 0 && (
                                contents[section.id].map((content) => (
                                    <div key={content.id} className="content-item">
                                        <div className="content-info">
                                            <span className={`content-type ${getContentTypeColor(content.contentType)}`}>
                                                {getContentTypeIcon(content.contentType)} {content.contentType}
                                            </span>
                                            <h4>{content.title}</h4>
                                            <p>{content.description}</p>
                                            {content.isPremium && (
                                                <span className="premium-badge">PREMIUM</span>
                                            )}
                                            {content.isFree && (
                                                <span className="free-badge">FREE</span>
                                            )}
                                        </div>
                                        <div className="content-actions">
                                            <button
                                                type="button"
                                                onClick={() => handleEditContent(content, section)}
                                                className="view-btn"
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteContent(content.id)}
                                                className="delete-btn"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* Show message if no content and no questions */}
                            {(!contents[section.id] || contents[section.id].length === 0) && 
                             (!questions[section.id] || questions[section.id].length === 0) && (
                                <div className="no-content">
                                    No content or questions added yet. Click "Add Content" to get started.
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AddContentForm = ({
    section,
    contentType,
    editMode,
    initialEditPayload,
    onSave,
    onCancel,
    uploadingVideo,
    setUploadingVideo,
    setMessage
}) => {
    const [activeTab, setActiveTab] = useState('java'); // For language tabs
    const [formData, setFormData] = useState(() => emptyFormState(contentType));

    useEffect(() => {
        if (initialEditPayload?.content) {
            setFormData(mapEditPayloadToForm(initialEditPayload.content, initialEditPayload.question));
        } else {
            setFormData(emptyFormState(contentType));
        }
    }, [initialEditPayload, contentType]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <div className="add-content-form">
            <div className="form-header">
                <h3>
                    {editMode ? 'Edit' : 'Add'} content — Section: {section.title}
                </h3>
                <button onClick={onCancel} className="cancel-btn">Cancel</button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Content Type:</label>
                    <select 
                        name="contentType" 
                        value={formData.contentType} 
                        onChange={handleChange}
                        disabled={editMode}
                    >
                        <option value="VIDEO">Video</option>
                        <option value="DOCUMENT">Document</option>
                        <option value="QUESTION">Question</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Title:</label>
                    <input 
                        type="text" 
                        name="title" 
                        value={formData.title} 
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Description:</label>
                    <textarea 
                        name="description" 
                        value={formData.description} 
                        onChange={handleChange}
                        rows="3"
                    />
                </div>

                <div className="form-group">
                    <label>Content Order:</label>
                    <input 
                        type="number" 
                        name="contentOrder" 
                        value={formData.contentOrder} 
                        onChange={handleChange}
                        min="1"
                    />
                </div>

                <div className="form-group checkbox-group">
                    <label>
                        <input 
                            type="checkbox" 
                            name="isFree" 
                            checked={formData.isFree} 
                            onChange={handleChange}
                        />
                        Free Content
                    </label>
                    <label>
                        <input 
                            type="checkbox" 
                            name="isPremium" 
                            checked={formData.isPremium} 
                            onChange={handleChange}
                        />
                        Premium Content
                    </label>
                </div>

                {formData.contentType === 'VIDEO' && (
                    <>
                        <div className="form-group">
                            <label>Video Source:</label>
                            <select 
                                name="videoSource" 
                                value={formData.videoSource || 'url'} 
                                onChange={handleChange}
                            >
                                <option value="url">External URL</option>
                                <option value="upload">Upload Video</option>
                            </select>
                        </div>
                        
                        {formData.videoSource === 'url' ? (
                            <>
                                <div className="form-group">
                                    <label>Video URL:</label>
                                    <input 
                                        type="url" 
                                        name="videoUrl" 
                                        value={formData.videoUrl} 
                                        onChange={handleChange}
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Duration (seconds):</label>
                                    <input 
                                        type="number" 
                                        name="videoDuration" 
                                        value={formData.videoDuration} 
                                        onChange={handleChange}
                                        placeholder="1200"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Upload Video File:</label>
                                    <input 
                                        type="file" 
                                        name="videoFile" 
                                        accept="video/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    videoFile: file,
                                                    videoUrl: '',
                                                    videoDuration: ''
                                                }));
                                            }
                                        }}
                                        required={
                                            formData.videoSource === 'upload' &&
                                            !(editMode && formData.videoUrl)
                                        }
                                    />
                                    <small>Supported formats: MP4, WebM, OGG (Max 100MB)</small>
                                </div>
                                {formData.videoFile && (
                                    <div className="upload-preview">
                                        <p>Selected file: {formData.videoFile.name}</p>
                                        <p>Size: {(formData.videoFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {formData.contentType === 'DOCUMENT' && (
                    <>
                        <div className="form-group">
                            <label>Document Source:</label>
                            <select
                                name="documentSource"
                                value={formData.documentSource || 'url'}
                                onChange={handleChange}
                            >
                                <option value="url">External URL</option>
                                <option value="upload">Upload Document</option>
                            </select>
                        </div>
                        
                        {formData.documentSource === 'url' ? (
                            <>
                                <div className="form-group">
                                    <label>Document URL:</label>
                                    <input 
                                        type="url" 
                                        name="documentUrl" 
                                        value={formData.documentUrl} 
                                        onChange={handleChange}
                                        placeholder="https://example.com/document.pdf"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Document Type:</label>
                                    <input 
                                        type="text" 
                                        name="documentType" 
                                        value={formData.documentType} 
                                        onChange={handleChange}
                                        placeholder="pdf, doc, ppt"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Document Size (bytes):</label>
                                    <input 
                                        type="number" 
                                        name="documentSize" 
                                        value={formData.documentSize} 
                                        onChange={handleChange}
                                        placeholder="1024000"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Upload Document File:</label>
                                    <input
                                        type="file"
                                        name="documentFile"
                                        accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    documentFile: file,
                                                    documentUrl: '',
                                                    documentType: file.name.split('.').pop()?.toLowerCase() || '',
                                                    documentSize: file.size
                                                }));
                                            }
                                        }}
                                        required={
                                            formData.documentSource === 'upload' &&
                                            !(editMode && formData.documentUrl)
                                        }
                                    />
                                    <small>Supported formats: PDF, DOC, DOCX, PPT, PPTX, TXT (Max 25MB)</small>
                                </div>
                                {formData.documentFile && (
                                    <div className="upload-preview">
                                        <p>Selected file: {formData.documentFile.name}</p>
                                        <p>Size: {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {formData.contentType === 'QUESTION' && (
                    <div className="question-fields">
                        <h4>Question Details</h4>
                        
                        <div className="field-row">
                            <div className="form-group">
                                <label>Difficulty Level:</label>
                                <select 
                                    name="difficultyLevel" 
                                    value={formData.difficultyLevel} 
                                    onChange={handleChange}
                                >
                                    <option value="EASY">Easy</option>
                                    <option value="MEDIUM">Medium</option>
                                    <option value="HARD">Hard</option>
                                    <option value="EXPERT">Expert</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Source Language:</label>
                                <select 
                                    name="sourceLanguage" 
                                    value={formData.sourceLanguage} 
                                    onChange={handleChange}
                                >
                                    <option value="java">Java</option>
                                    <option value="javascript">JavaScript</option>
                                    <option value="python">Python</option>
                                    <option value="cpp">C++</option>
                                    <option value="c">C</option>
                                    <option value="csharp">C#</option>
                                    <option value="php">PHP</option>
                                    <option value="ruby">Ruby</option>
                                    <option value="swift">Swift</option>
                                    <option value="go">Go</option>
                                    <option value="rust">Rust</option>
                                    <option value="kotlin">Kotlin</option>
                                    <option value="typescript">TypeScript</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Category Tags:</label>
                                <input 
                                    type="text" 
                                    name="categoryTags" 
                                    value={formData.categoryTags} 
                                    onChange={handleChange}
                                    placeholder="arrays, strings, algorithms"
                                />
                            </div>
                        </div>

                        <div className="field-row">
                            <div className="form-group">
                                <label>Time Limit (seconds):</label>
                                <input 
                                    type="number" 
                                    name="timeLimitSeconds" 
                                    value={formData.timeLimitSeconds} 
                                    onChange={handleChange}
                                    placeholder="30"
                                />
                            </div>
                            <div className="form-group">
                                <label>Memory Limit (MB):</label>
                                <input 
                                    type="number" 
                                    name="memoryLimitMB" 
                                    value={formData.memoryLimitMB} 
                                    onChange={handleChange}
                                    placeholder="256"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Problem Statement:</label>
                            <textarea 
                                name="problemStatement" 
                                value={formData.problemStatement} 
                                onChange={handleChange}
                                rows="4"
                                placeholder="Describe the problem clearly..."
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Expected Output:</label>
                            <textarea 
                                name="expectedOutput" 
                                value={formData.expectedOutput} 
                                onChange={handleChange}
                                rows="3"
                                placeholder="Describe what the output should be..."
                            />
                        </div>

                        <div className="form-group">
                            <label>Basic Test Cases (Visible - Run Code):</label>
                            <small>Format: input1, input2 | expected_output (one per line)</small>
                            <textarea 
                                name="testCases" 
                                value={formData.testCases} 
                                onChange={handleChange}
                                rows="5"
                                placeholder="anagram, nagaram | true&#10;rat, car | false&#10;listen, silent | true"
                                required
                            />
                            <small>3-5 test cases shown when users click "Run Code"</small>
                        </div>

                        <div className="form-group">
                            <label>Hidden Test Cases (Submit Only):</label>
                            <small>Format: input1, input2 | expected_output (one per line)</small>
                            <textarea 
                                name="hiddenTestCases" 
                                value={formData.hiddenTestCases} 
                                onChange={handleChange}
                                rows="8"
                                placeholder="anagram, nagaram | true&#10;rat, car | false&#10;a, a | true&#10;ab, ba | true&#10;... (15-20 cases including edge cases)"
                                required
                            />
                            <small>15-20 test cases that run when users click "Submit"</small>
                        </div>

                        <div className="form-group">
                            <label>Constraints:</label>
                            <textarea 
                                name="constraints" 
                                value={formData.constraints} 
                                onChange={handleChange}
                                rows="3"
                                placeholder="1 ≤ n ≤ 10^5\n1 ≤ arr[i] ≤ 10^9"
                            />
                        </div>

                        <div className="form-group">
                            <label>Examples:</label>
                            <textarea 
                                name="examples" 
                                value={formData.examples} 
                                onChange={handleChange}
                                rows="3"
                                placeholder="Example 1:\nInput: nums = [2,7,11,15], target = 9\nOutput: [0,1]"
                            />
                        </div>

                        <div className="form-group">
                            <label>Solution Explanation:</label>
                            <textarea 
                                name="solution" 
                                value={formData.solution} 
                                onChange={handleChange}
                                rows="4"
                                placeholder="Explain the solution approach..."
                            />
                        </div>

                        <div className="form-group">
                            <label>Solution Code:</label>
                            <textarea 
                                name="solutionCode" 
                                value={formData.solutionCode} 
                                onChange={handleChange}
                                rows="6"
                                placeholder="// Your solution code here\nfunction solve(nums, target) {\n    // implementation\n}"
                            />
                        </div>

                        <div className="form-group">
                            <label>Initial Code Templates (All Languages):</label>
                            <small style={{display: 'block', marginBottom: '10px', color: '#666'}}>
                                ⚠️ Provide complete code templates including imports, class, solution method, test runner, and stdin handling for each language
                            </small>
                            
                            <div style={{marginTop: '10px'}}>
                                <div style={{display: 'flex', gap: '5px', marginBottom: '10px', borderBottom: '2px solid #e0e0e0'}}>
                                    <button type="button" onClick={() => setActiveTab('java')} style={{padding: '6px 12px', border: 'none', background: activeTab === 'java' ? '#007bff' : '#f8f9fa', color: activeTab === 'java' ? 'white' : '#333', cursor: 'pointer'}}>Java</button>
                                    <button type="button" onClick={() => setActiveTab('python')} style={{padding: '6px 12px', border: 'none', background: activeTab === 'python' ? '#007bff' : '#f8f9fa', color: activeTab === 'python' ? 'white' : '#333', cursor: 'pointer'}}>Python</button>
                                    <button type="button" onClick={() => setActiveTab('javascript')} style={{padding: '6px 12px', border: 'none', background: activeTab === 'javascript' ? '#007bff' : '#f8f9fa', color: activeTab === 'javascript' ? 'white' : '#333', cursor: 'pointer'}}>JavaScript</button>
                                    <button type="button" onClick={() => setActiveTab('cpp')} style={{padding: '6px 12px', border: 'none', background: activeTab === 'cpp' ? '#007bff' : '#f8f9fa', color: activeTab === 'cpp' ? 'white' : '#333', cursor: 'pointer'}}>C++</button>
                                    <button type="button" onClick={() => setActiveTab('c')} style={{padding: '6px 12px', border: 'none', background: activeTab === 'c' ? '#007bff' : '#f8f9fa', color: activeTab === 'c' ? 'white' : '#333', cursor: 'pointer'}}>C</button>
                                </div>
                                
                                {activeTab === 'java' && (
                                    <textarea 
                                        name="initialCodeJava" 
                                        value={formData.initialCodeJava} 
                                        onChange={handleChange}
                                        rows="12"
                                        style={{width: '100%', fontFamily: 'monospace'}}
                                        placeholder="import java.util.*;\n\npublic class Solution {\n    public boolean isValid(String s) {\n        // Write your code here\n        return false;\n    }\n    \n    public static void main(String[] args) {\n        Scanner scanner = new Scanner(System.in);\n        String input = scanner.nextLine().trim();\n        Solution solution = new Solution();\n        System.out.println(solution.isValid(input));\n        scanner.close();\n    }\n}"
                                    />
                                )}
                                
                                {activeTab === 'python' && (
                                    <textarea 
                                        name="initialCodePython" 
                                        value={formData.initialCodePython} 
                                        onChange={handleChange}
                                        rows="12"
                                        style={{width: '100%', fontFamily: 'monospace'}}
                                        placeholder="class Solution:\n    def is_valid(self, s):\n        # Write your code here\n        return False\n\nif __name__ == '__main__':\n    import sys\n    input_data = sys.stdin.readline().strip()\n    solution = Solution()\n    print(solution.is_valid(input_data))"
                                    />
                                )}
                                
                                {activeTab === 'javascript' && (
                                    <textarea 
                                        name="initialCodeJavaScript" 
                                        value={formData.initialCodeJavaScript} 
                                        onChange={handleChange}
                                        rows="12"
                                        style={{width: '100%', fontFamily: 'monospace'}}
                                        placeholder="class Solution {\n    isValid(s) {\n        // Write your code here\n        return false;\n    }\n}\n\nconst readline = require('readline');\nconst rl = readline.createInterface({\n    input: process.stdin,\n    output: process.stdout\n});\n\nrl.on('line', (input) => {\n    const solution = new Solution();\n    console.log(solution.isValid(input.trim()));\n    rl.close();\n});"
                                    />
                                )}
                                
                                {activeTab === 'cpp' && (
                                    <textarea 
                                        name="initialCodeCpp" 
                                        value={formData.initialCodeCpp} 
                                        onChange={handleChange}
                                        rows="12"
                                        style={{width: '100%', fontFamily: 'monospace'}}
                                        placeholder="Paste your C++ template here with includes, Solution class, and main method"
                                    />
                                )}
                                
                                {activeTab === 'c' && (
                                    <textarea 
                                        name="initialCodeC" 
                                        value={formData.initialCodeC} 
                                        onChange={handleChange}
                                        rows="12"
                                        style={{width: '100%', fontFamily: 'monospace'}}
                                        placeholder="Paste your C template here with includes, function, and main method"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Related Topics:</label>
                            <input 
                                type="text" 
                                name="relatedTopics" 
                                value={formData.relatedTopics} 
                                onChange={handleChange}
                                placeholder="arrays, hash tables, two pointers"
                            />
                        </div>

                        <div className="form-group">
                            <label>Follow-up Questions:</label>
                            <textarea 
                                name="followUp" 
                                value={formData.followUp} 
                                onChange={handleChange}
                                rows="3"
                                placeholder="What if the array is sorted? What if we need to find all pairs?"
                            />
                        </div>

                        <div className="form-group">
                            <label>Video Link (Solution Video):</label>
                            <input 
                                type="url" 
                                name="videoLink" 
                                value={formData.videoLink} 
                                onChange={handleChange}
                                placeholder="https://www.youtube.com/watch?v=..."
                            />
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <button 
                        type="submit" 
                        className="save-btn" 
                        disabled={uploadingVideo}
                        style={{
                            opacity: uploadingVideo ? 0.6 : 1,
                            cursor: uploadingVideo ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {uploadingVideo ? '⏳ Uploading...' : editMode ? 'Save changes' : 'Add Content'}
                    </button>
                    <button 
                        type="button" 
                        onClick={onCancel} 
                        className="cancel-btn"
                        disabled={uploadingVideo}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

// Session Form Component
const SessionForm = ({ course, section, existingSession, onSave, onCancel, setMessage }) => {
    const [formData, setFormData] = useState({
        title: existingSession?.title || '',
        description: existingSession?.description || '',
        sessionNumber: existingSession?.sessionNumber || 1,
        scheduledDate: existingSession?.scheduledDate ? existingSession.scheduledDate.substring(0, 16) : '',
        durationMinutes: existingSession?.durationMinutes || 90,
        meetingLink: existingSession?.meetingLink || '',
        meetingPlatform: existingSession?.meetingPlatform || 'Zoom',
        meetingId: existingSession?.meetingId || '',
        meetingPassword: existingSession?.meetingPassword || '',
        topicsCovered: existingSession?.topicsCovered || '',
        status: existingSession?.status || 'SCHEDULED'
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const token = localStorage.getItem('token');
            const url = existingSession 
                ? `${API_URL}/api/live-sessions/${existingSession.id}`
                : `${API_URL}/api/live-sessions`;
            
            const method = existingSession ? 'PUT' : 'POST';

            const sessionData = {
                ...formData,
                sessionNumber: parseInt(formData.sessionNumber),
                durationMinutes: parseInt(formData.durationMinutes),
                course: { id: course.id },
                section: { id: section.id }
            };

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(sessionData)
            });

            if (response.ok) {
                const session = await response.json();
                onSave(session);
            } else {
                const errorText = await response.text();
                setMessage('Failed to save session: ' + errorText);
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (error) {
            setMessage('Error saving session: ' + error.message);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    return (
        <div className="content-manager">
            <div className="header">
                <button onClick={onCancel} className="back-btn">
                    ← Back
                </button>
                <h2>
                    {existingSession ? 'Edit' : 'Schedule'} Live Session for Section: {section?.title}
                </h2>
            </div>

            <form onSubmit={handleSubmit} style={{maxWidth: '800px', margin: '0 auto', padding: '20px'}}>
                <div className="form-section-header">
                    <h3>Session Details</h3>
                </div>

                <div className="form-group">
                    <label htmlFor="title">Session Title *</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Introduction to React Hooks"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="3"
                        placeholder="What will be covered in this session?"
                    />
                </div>

                <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                    <div className="form-group">
                        <label htmlFor="scheduledDate">Scheduled Date & Time *</label>
                        <input
                            type="datetime-local"
                            id="scheduledDate"
                            name="scheduledDate"
                            value={formData.scheduledDate}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="durationMinutes">Duration (minutes) *</label>
                        <input
                            type="number"
                            id="durationMinutes"
                            name="durationMinutes"
                            value={formData.durationMinutes}
                            onChange={handleChange}
                            required
                            min="15"
                            placeholder="90"
                        />
                    </div>
                </div>

                <div className="form-section-header">
                    <h3>Meeting Details</h3>
                </div>

                <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                    <div className="form-group">
                        <label htmlFor="meetingPlatform">Meeting Platform *</label>
                        <select
                            id="meetingPlatform"
                            name="meetingPlatform"
                            value={formData.meetingPlatform}
                            onChange={handleChange}
                            required
                        >
                            <option value="Zoom">Zoom</option>
                            <option value="Google Meet">Google Meet</option>
                            <option value="Microsoft Teams">Microsoft Teams</option>
                            <option value="Webex">Webex</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <select
                            id="status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="SCHEDULED">Scheduled</option>
                            <option value="LIVE_NOW">Live Now</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="meetingLink">Meeting Link *</label>
                    <input
                        type="url"
                        id="meetingLink"
                        name="meetingLink"
                        value={formData.meetingLink}
                        onChange={handleChange}
                        required
                        placeholder="https://zoom.us/j/123456789"
                    />
                </div>

                <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                    <div className="form-group">
                        <label htmlFor="meetingId">Meeting ID</label>
                        <input
                            type="text"
                            id="meetingId"
                            name="meetingId"
                            value={formData.meetingId}
                            onChange={handleChange}
                            placeholder="123 456 789"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="meetingPassword">Meeting Password</label>
                        <input
                            type="text"
                            id="meetingPassword"
                            name="meetingPassword"
                            value={formData.meetingPassword}
                            onChange={handleChange}
                            placeholder="Optional"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="topicsCovered">Topics to be Covered</label>
                    <textarea
                        id="topicsCovered"
                        name="topicsCovered"
                        value={formData.topicsCovered}
                        onChange={handleChange}
                        rows="3"
                        placeholder="List the main topics that will be covered"
                    />
                </div>

                <div className="form-actions" style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                    <button 
                        type="submit" 
                        className="save-btn"
                        style={{flex: 1, padding: '12px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer'}}
                    >
                        {existingSession ? 'Update Session' : 'Schedule Session'}
                    </button>
                    <button 
                        type="button" 
                        onClick={onCancel}
                        className="cancel-btn"
                        style={{flex: 1, padding: '12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer'}}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CourseContentManager; 
